import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightElement,
  Radio,
  RadioGroup,
  Select,
  Stack,
  useToast,
} from "@chakra-ui/react";
import React, { Dispatch, SetStateAction, useMemo } from "react";
import { GrFormCheckmark, GrFormClose } from "react-icons/gr";
import { useHistory } from "react-router-dom";
import {
  EmploymentStatus,
  Gender,
  OutreachSource,
  OutreachSourceType,
  PhoneType,
  ProLeadSource,
  ReasonForJoiningLaborHack,
} from "@/types";
import RequestFailed from "@/_components/RequestFailed";
import { nonEmptyString } from "../../helpers/helpers";
import { Select as CRSelect } from "chakra-react-select";

const employmentStatusOptions = [
  { label: "Employed", value: EmploymentStatus.EMPLOYED },
  { label: "Self Employed", value: EmploymentStatus.SELF_EMPLOYED },
  { label: "Unemployed", value: EmploymentStatus.UNEMPLOYED },
];

const phoneTypeOptions = [
  {
    label: "Yes, I have a phone that can use WhatsApp or browse the internet",
    value: PhoneType.SMARTPHONE,
  },
  {
    label: "No, my phone can only make calls and send text messages",
    value: PhoneType.BASIC_PHONE,
  },
  { label: "I don't have a phone at the moment", value: PhoneType.NO_PHONE },
];

const reasonForJoiningLaborHackOptions = [
  {
    label: "My personal portfolio",
    value: ReasonForJoiningLaborHack.MY_PERSONAL_PORTFOLIO,
  },
  { label: "Invoicing tool", value: ReasonForJoiningLaborHack.INVOICING_TOOL },
  { label: "Tool financing", value: ReasonForJoiningLaborHack.TOOL_FINANCING },
  { label: "Local jobs", value: ReasonForJoiningLaborHack.LOCAL_JOBS },
  {
    label: "International jobs",
    value: ReasonForJoiningLaborHack.INTERNATIONAL_JOBS,
  },
  {
    label: "Certifications",
    value: ReasonForJoiningLaborHack.CERTIFICATIONS,
  },
  { label: "Other", value: ReasonForJoiningLaborHack.OTHER },
];

const currencies = [{ label: "Nigerian Naira (NGN)", value: "Naira" }];

const leadSourceOptions = [
  { label: "Radio", value: ProLeadSource.RADIO },
  { label: "From a friend", value: ProLeadSource.FROM_A_FRIEND },
  { label: "Flyer", value: ProLeadSource.FLYER },
  { label: "Social Media", value: ProLeadSource.SOCIAL_MEDIA },
  { label: "Google Search", value: ProLeadSource.GOOGLE },
  { label: "OPL Academy", value: ProLeadSource.OPL_ACADEMY },
  {
    label: "LaborHack Community Outreach Team",
    value: ProLeadSource.COMMUNITY_OUTREACH,
  },
  { label: "Other", value: ProLeadSource.OTHER },
];

const countries: readonly string[] = ["Nigeria"];

const CREATE_NEW_CHANNEL = 'CREATE_NEW_CHANNEL';

const FETCH_OUTREACH_SOURCES = gql`
  query FetchOutreachSources {
    fetchOutreachSources {
      id
      name
      type
    }
  }
`;

const CREATE_PRO_APPLICATION = gql`
  mutation CreateProApplication($input: SubmitProApplicationInput!) {
    createProApplication(input: $input) {
      id
    }
  }
`;

const JOB_TAGS = gql`
  query JobAdTags {
    jobAdTags {
      id
      name
      group
    }
  }
`;

const CREATE_OUTREACH_SOURCE = gql`
  mutation CreateOutreachSource($payload: OutreachSourceInput!) {
    createOutreachSource(payload: $payload) {
      id
      name
      type
    }
  }
`;

export interface ApplicationFormProps {}

export const ApplicationForm = () => {
  const { push } = useHistory();
  const toast = useToast();

  const [
    createOutreachSource,
    { error: newOutreachSourceError, reset: resetCreateOutreachSourceError },
  ] = useMutation(CREATE_OUTREACH_SOURCE, {
    onCompleted: async (data) => {
      setIsRefetching(true);

      await refetchOutreachSources();

      if (data?.createOutreachSource.type === OutreachSourceType.CHANNEL) {
        setChannel(data?.createOutreachSource.id);
      }

      setIsRefetching(false);
    },
  });

  const [createProApplication, createProApplicationResponse] = useMutation(
    CREATE_PRO_APPLICATION,
    {
      onCompleted: (data) => {
        toast({
          title: 'Pro Created',
          description: 'The application has been submitted successfully.',
          status: 'success',
          duration: 9000,
          isClosable: true,
          position: 'top',
        });

        push('/pros');
      },
    }
  );

  const { error: tagsError, data: tagsData, loading: loadingTags } = useQuery<{
    jobAdTags: { id: string; name: string; group: string }[];
  }>(JOB_TAGS);

  const tagOptions = useMemo(() => {
    if (!tagsData?.jobAdTags) {
      return [];
    }

    return tagsData.jobAdTags.map((tag) => ({
      label: tag.name,
      value: tag.id,
    }));
  }, [tagsData?.jobAdTags]);

  const {
    loading: loadingOutreachSources,
    data: outreachSourcesData,
    refetch: refetchOutreachSources,
  } = useQuery<{ fetchOutreachSources: OutreachSource[] }>(
    FETCH_OUTREACH_SOURCES
  );

  const outreachChannels = useMemo(() => {
    return outreachSourcesData?.fetchOutreachSources.filter(({ type }) => {
      return type === OutreachSourceType.CHANNEL;
    });
  }, [outreachSourcesData]);

  /** States to hold form data */
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [dateOfBirth, setDateOfBirth] = React.useState<string>();
  const [gender, setGender] = React.useState('');
  const [country, setCountry] = React.useState("Nigeria");

  // New fields matching pro registration
  const [jobTitle, setJobTitle] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);
  const [address, setAddress] = React.useState('');
  const [stateOfResidence, setStateOfResidence] = React.useState('');
  const [employmentStatus, setEmploymentStatus] = React.useState<
    EmploymentStatus | ""
  >("");
  const [
    averageMonthlyIncome,
    setAverageMonthlyIncome,
  ] = React.useState<number>(0);
  const [incomeCurrency, setIncomeCurrency] = React.useState<string>(
    currencies[0].value,
  );
  const [experienceLevel, setExperienceLevel] = React.useState<string>('');
  const [phoneType, setPhoneType] = React.useState<PhoneType | "">("");

  const [
    numberOfDaysActivePerWeek,
    setNumberOfDaysActivePerWeek,
  ] = React.useState<number>(0);
  const [leadSource, setLeadSource] = React.useState<string>(
    ProLeadSource.COMMUNITY_OUTREACH
  );
  const [leadSourceText, setLeadSourceText] = React.useState("");
  const [
    reasonsForJoiningLaborHack,
    setReasonsForJoiningLaborHack,
  ] = React.useState<ReasonForJoiningLaborHack[]>([]);
  const [
    reasonForJoiningLaborHackOtherText,
    setReasonForJoiningLaborHackOtherText,
  ] = React.useState("");
  const [channel, setChannel] = React.useState<string>('');
  const [otherChannel, setOtherChannel] = React.useState<string>('');
  const [isRefetching, setIsRefetching] = React.useState<boolean>(false);

  if (loadingOutreachSources) {
    return null;
  }

  const getStringFieldChangeHandler = (
    handler: Dispatch<SetStateAction<string>>
  ) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      handler(e.target.value);
    };
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value.trim());
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value.trim());
  };

  const handleCreateNewChannel = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    createOutreachSource({
      variables: {
        payload: {
          name: otherChannel.trim(),
          type: OutreachSourceType.CHANNEL,
        },
      },
    });
  };

  const isValidPhoneNumber = (phoneNumberString: string) => {
    const cleaned = phoneNumberString.trim();

    // Check if the cleaned number is 10 digits long
    if (cleaned.length === 10) {
      return true;
    }

    // If it's not 10 digits long, it's not a number.
    return false;
  };

  if (tagsError) {
    return <RequestFailed errorMessage={tagsError.message} />;
  }

  if (createProApplicationResponse.error || newOutreachSourceError) {
    return (
      <RequestFailed
        errorMessage={
          createProApplicationResponse?.error?.message ||
          newOutreachSourceError?.message
        }
        onRetry={() => {
          createProApplicationResponse.reset();
          resetCreateOutreachSourceError();
        }}
      />
    );
  }

  const hasAtLeastOneTag = tags.length > 0;
  const incomeIsValid =
    employmentStatus === EmploymentStatus.UNEMPLOYED ||
    averageMonthlyIncome > 0;

  const canSubmit =
    firstName &&
    lastName &&
    isValidPhoneNumber(phone) &&
    !!email &&
    dateOfBirth &&
    gender &&
    country &&
    address &&
    stateOfResidence &&
    jobTitle &&
    hasAtLeastOneTag &&
    employmentStatus &&
    incomeIsValid &&
    incomeCurrency &&
    experienceLevel &&
    numberOfDaysActivePerWeek !== undefined &&
    phoneType &&
    leadSource &&
    (leadSource === ProLeadSource.OTHER ? leadSourceText : true) &&
    reasonsForJoiningLaborHack.length > 0 &&
    (reasonsForJoiningLaborHack.includes(ReasonForJoiningLaborHack.OTHER)
      ? reasonForJoiningLaborHackOtherText
      : true) &&
    channel !== CREATE_NEW_CHANNEL;

  const handleSubmit = () => {
    const input = {
      firstName,
      lastName,
      email,
      phoneNumber: "+234" + phone,
      dateOfBirth: new Date(dateOfBirth!).toISOString(),
      gender,
      country,
      address,
      stateOfResidence,
      jobTitle,
      tags,
      employmentStatus,
      averageMonthlyIncome:
        employmentStatus === EmploymentStatus.UNEMPLOYED
          ? 0
          : averageMonthlyIncome,
      incomeCurrency,
      yearsOfExperience: Number(experienceLevel),
      numberOfDaysWorkedPerWeek: numberOfDaysActivePerWeek,
      phoneType,
      leadSource,
      ...(leadSource === ProLeadSource.OTHER && { leadSourceText }),
      reasonsForJoiningLaborHack,
      ...(reasonsForJoiningLaborHack.includes(ReasonForJoiningLaborHack.OTHER) && {
        reasonForJoiningLaborHackOtherText,
      }),
      ...(channel &&
        channel !== CREATE_NEW_CHANNEL && { channelOutreachSourceId: channel }),
    };

    createProApplication({ variables: { input } });
  };

  return (
    <>
      <div className="grid !grid-cols-1 md:!grid-cols-2 gap-y-4 w-full">
        <div>
          <h3 className="font-semibold text-primary-500">
            Personal Information
          </h3>
          <p className="text-sm text-primary-300">
            Provide your personal information
          </p>
        </div>
        <Stack spacing={2} className="px-2 md:px-4">
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel fontSize={"sm"} htmlFor="firstName" color="primary.500">
              First Name
            </FormLabel>
            <Input
              id="firstName"
              fontSize={"sm"}
              type={"text"}
              value={firstName}
              onChange={handleFirstNameChange}
              placeholder="Tolu"
            />
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel fontSize={"sm"} htmlFor="lastName" color="primary.500">
              Last Name
            </FormLabel>
            <Input
              id="lastName"
              fontSize={"sm"}
              type={"text"}
              value={lastName}
              onChange={handleLastNameChange}
              placeholder="Holmes"
            />
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel fontSize={"sm"} color="primary.500">
              Date of Birth
            </FormLabel>
            <Input
              id="dateOfBirth"
              fontSize={"sm"}
              type={"date"}
              alignItems="center"
              value={dateOfBirth}
              onChange={(event) => {
                setDateOfBirth(event.target.value);
              }}
            />
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            as="fieldset"
          >
            <FormLabel fontSize={"sm"} as="legend">
              Sex
            </FormLabel>
            <RadioGroup
              colorScheme={"brand"}
              value={gender}
              onChange={setGender}
            >
              <Stack spacing={2}>
                <Radio size={"sm"} value={Gender.MALE}>
                  Male
                </Radio>
                <Radio size={"sm"} value={Gender.FEMALE}>
                  Female
                </Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
        </Stack>
      </div>
      <div className="my-4 md:my-8 w-full h-0.5 bg-primary-100" />
      <div className="grid !grid-cols-1 md:!grid-cols-2 gap-y-4 w-full ">
        <div>
          <h3 className="font-semibold text-primary-500">
            Contact Information
          </h3>
          <p className="text-sm text-primary-300">
            Provide your contact information
          </p>
        </div>
        <Stack spacing={2} className="px-2 md:px-4">
          <FormControl isRequired>
            <FormLabel fontSize={"sm"} htmlFor="email" color="primary.500">
              Email Address
            </FormLabel>
            <InputGroup>
              <Input
                id="email"
                type={"email"}
                fontSize={"sm"}
                value={email}
                onChange={getStringFieldChangeHandler(setEmail)}
              />
            </InputGroup>
          </FormControl>
          <FormControl
            isRequired
            isDisabled={createProApplicationResponse.loading}
            isInvalid={nonEmptyString(phone) && !isValidPhoneNumber(phone)}
          >
            <FormLabel fontSize={"sm"} htmlFor="phone" color="primary.500">
              Phone Number
            </FormLabel>
            <InputGroup>
              <InputLeftAddon fontSize={"sm"} children="+234" />
              <Input
                id="phone"
                type={"phone"}
                fontSize={"sm"}
                value={phone}
                onChange={getStringFieldChangeHandler(setPhone)}
                placeholder="80312345678"
              />
              {nonEmptyString(phone) && (
                <InputRightElement
                  children={
                    isValidPhoneNumber(phone) ? (
                      <Icon
                        as={GrFormCheckmark}
                        w="6"
                        h="6"
                        color={"green.500"}
                      />
                    ) : (
                      <Icon as={GrFormClose} w="6" h="6" color={"red.500"} />
                    )
                  }
                />
              )}
            </InputGroup>
            <FormHelperText>
              {!isValidPhoneNumber(phone) && (
                <FormErrorMessage>
                  Please enter a valid phone number
                </FormErrorMessage>
              )}
            </FormHelperText>
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel fontSize={"sm"} htmlFor="address" color="primary.500">
              Address
            </FormLabel>
            <Input
              id="address"
              fontSize={"sm"}
              value={address}
              onChange={getStringFieldChangeHandler(setAddress)}
              placeholder="1234, Main Street"
            />
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel
              fontSize={"sm"}
              htmlFor="stateOfResidence"
              color="primary.500"
            >
              State of Residence
            </FormLabel>
            <Input
              id="stateOfResidence"
              fontSize={"sm"}
              value={stateOfResidence}
              onChange={getStringFieldChangeHandler(setStateOfResidence)}
              placeholder="Lagos"
            />
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
            isInvalid={!country}
          >
            <FormLabel fontSize={"sm"} htmlFor="country" color="primary.500">
              Country
            </FormLabel>
            <CRSelect
              id="country"
              placeholder="Select option"
              defaultInputValue={country}
              options={countries.map((country) => ({
                label: country,
                value: country,
              }))}
              onChange={(selectedOption) => {
                if (selectedOption) setCountry(selectedOption.value);
              }}
            />

            {!country && (
              <FormErrorMessage fontSize={"sm"}>
                Please select your country
              </FormErrorMessage>
            )}
          </FormControl>
        </Stack>
      </div>
      <div className="my-4 md:my-8 w-full h-0.5 bg-primary-100" />
      <div className="grid !grid-cols-1 md:!grid-cols-2 gap-y-4 w-full ">
        <div>
          <h3 className="font-semibold text-primary-500">
            Professional Information
          </h3>
          <p className="text-sm text-primary-300">
            Provide your professional information
          </p>
        </div>
        <Stack spacing={2} className="px-2 md:px-4">
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel fontSize={"sm"} htmlFor="jobTitle" color="primary.500">
              Profession
            </FormLabel>
            <Input
              id="jobTitle"
              fontSize={"sm"}
              value={jobTitle}
              onChange={getStringFieldChangeHandler(setJobTitle)}
              placeholder="e.g. Electrician, Plumber, Carpenter"
            />
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading || loadingTags}
            isRequired
          >
            <FormLabel fontSize={"sm"} htmlFor="tags" color="primary.500">
              Skills / Tags
            </FormLabel>
            <CRSelect
              id="tags"
              isMulti
              placeholder="Select skills"
              options={tagOptions}
              value={tagOptions.filter((opt) => tags.includes(opt.value))}
              onChange={(selected) => {
                setTags(selected ? selected.map((s: any) => s.value) : []);
              }}
            />
            <FormHelperText fontSize={"xs"}>
              Select at least one skill tag
            </FormHelperText>
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel fontSize={"sm"} htmlFor="experience" color="primary.500">
              How many years of experience do you have?
            </FormLabel>
            <Input
              id="experience"
              type="number"
              fontSize={"sm"}
              min={0}
              value={experienceLevel}
              onChange={getStringFieldChangeHandler(setExperienceLevel)}
            />
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel
              fontSize={"sm"}
              htmlFor="employmentStatus"
              color="primary.500"
            >
              Employment Status
            </FormLabel>
            <Select
              id="employmentStatus"
              fontSize={"sm"}
              placeholder="Select option"
              value={employmentStatus}
              onChange={(event) =>
                setEmploymentStatus(event.target.value as EmploymentStatus)
              }
            >
              {employmentStatusOptions.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormControl>
          {employmentStatus &&
            employmentStatus !== EmploymentStatus.UNEMPLOYED && (
              <>
                <FormControl
                  isDisabled={createProApplicationResponse.loading}
                  isRequired
                >
                  <FormLabel
                    fontSize={"sm"}
                    htmlFor="averageMonthlyIncome"
                    color="primary.500"
                  >
                    Average Monthly Income
                  </FormLabel>
                  <Input
                    id="averageMonthlyIncome"
                    type="number"
                    fontSize={"sm"}
                    min={0}
                    value={averageMonthlyIncome}
                    onChange={(e) =>
                      setAverageMonthlyIncome(Number(e.target.value))
                    }
                    placeholder="e.g. 100000"
                  />
                </FormControl>
                <FormControl
                  isDisabled={createProApplicationResponse.loading}
                  isRequired
                >
                  <FormLabel
                    fontSize={"sm"}
                    htmlFor="incomeCurrency"
                    color="primary.500"
                  >
                    Income Currency
                  </FormLabel>
                  <Select
                    id="incomeCurrency"
                    fontSize={"sm"}
                    value={incomeCurrency}
                    onChange={(event) => setIncomeCurrency(event.target.value)}
                  >
                    {currencies.map(({ label, value }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            as="fieldset"
          >
            <FormLabel fontSize={"sm"} as="legend" color="primary.500">
              On average, How many days do you work per week?
            </FormLabel>
            <Select
              fontSize={"sm"}
              placeholder="Select option"
              value={numberOfDaysActivePerWeek}
              onChange={(event) =>
                setNumberOfDaysActivePerWeek(Number(event.target.value))
              }
            >
              {[0, 1, 2, 3, 4, 5, 6, 7].map((value) => {
                return (
                  <option key={value} value={value}>
                    {value}
                  </option>
                );
              })}
            </Select>
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel fontSize={"sm"} htmlFor="phoneType" color="primary.500">
              Do you have a smartphone?
            </FormLabel>
            <Select
              id="phoneType"
              fontSize={"sm"}
              placeholder="Select option"
              value={phoneType}
              onChange={(event) =>
                setPhoneType(event.target.value as PhoneType)
              }
            >
              {phoneTypeOptions.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel
              fontSize={"sm"}
              htmlFor="reasonForJoiningLaborHack"
              color="primary.500"
            >
              Why are you joining LaborHack? (select all that apply)
            </FormLabel>
            <div className="flex flex-wrap gap-2">
              {reasonForJoiningLaborHackOptions.map(({ label, value }) => {
                const isSelected = reasonsForJoiningLaborHack.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={createProApplicationResponse.loading}
                    onClick={() => {
                      setReasonsForJoiningLaborHack((prev) =>
                        prev.includes(value)
                          ? prev.filter((r) => r !== value)
                          : [...prev, value]
                      );
                    }}
                    className={`
                      px-3 py-2 rounded-full border text-sm font-medium
                      transition-colors duration-150 cursor-pointer
                      ${
                        isSelected
                          ? "bg-gray-800 text-white border-gray-800"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-800"
                      }
                    `}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </FormControl>
          {reasonsForJoiningLaborHack.includes(ReasonForJoiningLaborHack.OTHER) && (
            <FormControl
              isDisabled={createProApplicationResponse.loading}
              isRequired
            >
              <FormLabel
                fontSize={"sm"}
                htmlFor="reasonForJoiningLaborHackOtherText"
                color="primary.500"
              >
                Please specify why you are joining LaborHack
              </FormLabel>
              <Input
                id="reasonForJoiningLaborHackOtherText"
                fontSize={"sm"}
                value={reasonForJoiningLaborHackOtherText}
                onChange={getStringFieldChangeHandler(
                  setReasonForJoiningLaborHackOtherText,
                )}
              />
            </FormControl>
          )}
          <FormControl
            isDisabled={createProApplicationResponse.loading}
            isRequired
          >
            <FormLabel fontSize={"sm"} htmlFor="leadSource" color="primary.500">
              How did you hear about us?
            </FormLabel>
            <Select
              fontSize={"sm"}
              placeholder="Select option"
              value={leadSource}
              onChange={(event) => setLeadSource(event.target.value)}
            >
              {leadSourceOptions.map(({ label, value }) => {
                return (
                  <option key={label} value={value}>
                    {label}
                  </option>
                );
              })}
            </Select>
          </FormControl>
          {leadSource === ProLeadSource.OTHER && (
            <FormControl
              isDisabled={createProApplicationResponse.loading}
              isRequired
            >
              <FormLabel
                fontSize={"sm"}
                htmlFor="leadSourceText"
                color="primary.500"
              >
                Please specify how you heard about us
              </FormLabel>
              <Input
                id="leadSourceText"
                fontSize={"sm"}
                value={leadSourceText}
                onChange={getStringFieldChangeHandler(setLeadSourceText)}
              />
            </FormControl>
          )}
          <FormControl isDisabled={createProApplicationResponse.loading}>
            <FormLabel fontSize={"sm"} htmlFor="leadSource" color="primary.500">
              Channel
            </FormLabel>
            <Select
              fontSize={"sm"}
              placeholder="Select option"
              value={channel}
              onChange={(event) => setChannel(event.target.value)}
            >
              {outreachChannels?.map((channel: OutreachSource) => {
                return (
                  <option key={channel.id} value={channel.id}>
                    {channel.name}
                  </option>
                );
              })}
              <option key={CREATE_NEW_CHANNEL} value={CREATE_NEW_CHANNEL}>
                Create New Channel
              </option>
            </Select>
          </FormControl>
          {channel === CREATE_NEW_CHANNEL && (
            <FormControl
              isDisabled={createProApplicationResponse.loading}
              isRequired
            >
              <FormLabel
                fontSize={"sm"}
                htmlFor="leadSourceOther"
                color="primary.500"
              >
                New Channel Name
              </FormLabel>
              <InputGroup size="md">
                <Input
                  id="channel-input"
                  fontSize={"sm"}
                  value={otherChannel}
                  onChange={(event) => setOtherChannel(event.target.value)}
                />
                <InputRightElement width="12.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    isLoading={isRefetching}
                    onClick={handleCreateNewChannel}
                  >
                    Create New Channel
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
          )}
        </Stack>
      </div>
      <div className="my-4 md:my-8 w-full h-0.5 bg-primary-100" />
      <div className="flex justify-end">
        <Button
          fontSize={"sm"}
          colorScheme={"red"}
          variant="ghost"
          className="mr-2"
        >
          Cancel
        </Button>
        <Button
          isLoading={createProApplicationResponse.loading}
          isDisabled={!canSubmit}
          loadingText="Submitting"
          fontSize={"sm"}
          colorScheme={"green"}
          variant={"solid"}
          spinnerPlacement="start"
          onClick={handleSubmit}
        >
          Submit Application
        </Button>
      </div>
    </>
  );
};
